import { Injectable } from '@angular/core';
import { OverlayService } from '@clavisco/overlay';
import { IPermission } from '../interfaces/i-permission';
import { ActivatedRouteSnapshot } from '@angular/router';
import { EnumList } from '../interfaces/i-enum';

/**
 * GlobalService provides utility methods for handling enums, permissions, routes, and formatting.
 * It includes functionality to convert enums into lists, validate permissions, format amounts and dates, and retrieve route paths.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalService {
  /**
   * Constructs the GlobalService.
   *
   * @param {OverlayService} clBlockUI - Service for managing overlay operations.
   */
  constructor(private clBlockUI: OverlayService) {}

  /**
   * Converts an enum into a list of objects with id and name properties.
   *
   * @template T - The type of the enum.
   * @param {T} enumType - The enum to convert.
   * @param {Record<string, string>} [translations] - Optional translations for enum keys.
   * @returns {EnumList[]} A list of objects representing the enum.
   */
  ConvertEnumIntoList<T extends Record<string, string | number>>(
    enumType: T,
    translations?: Record<string, string>
  ): EnumList[] {
    let map: EnumList[] = [];

    for (const key in enumType) {
      if (!isNaN(Number(key))) continue; // Ignore numeric keys (reverse mapping of enums)

      map.push({
        id: enumType[key] as number,
        name: translations?.[key] || key, // Use translation if available, otherwise use enum key
      });
    }

    return map;
  }

  /**
   * Retrieves the required permission string based on the route.
   *
   * @param {string} route - The route path.
   * @returns {string} The required permission string for the route.
   */
  GetPermissionsByPath(route: string): string {
    let reqPerm: string = '';
    switch (route) {
      case 'settings':
        reqPerm = 'M_Settings';
        break;
      case 'users':
        reqPerm = 'S_Users';
        break;
      case 'roles':
        reqPerm = 'S_RolsPerm';
        break;
      case 'connections':
        reqPerm = 'S_Connections';
        break;
      case 'companies':
        reqPerm = 'S_Companies';
        break;
      case 'documents':
        reqPerm = 'M_Documents';
        break;
      case 'documents-list':
        reqPerm = 'S_DocumentsList';
        break;
      case 'logs-documents':
        reqPerm = 'S_LogsList';
        break;
      case 'logsViewer':
        reqPerm = 'S_PhysicalLogs';
        break;
      default:
        break;
    }
    return reqPerm;
  }

  /**
   * Validates if the required permission exists in the provided permissions list.
   *
   * @param {IPermission[] | null} perms - The list of permissions.
   * @param {string} reqPerm - The required permission string.
   * @returns {boolean} True if the required permission exists, false otherwise.
   */
  ValidatePermission(perms: IPermission[] | null, reqPerm: string): boolean {
    if (!perms) return false;
    return perms.some((p: IPermission) => p.Name === reqPerm);
  }

  /**
   * Retrieves the full route path from an ActivatedRouteSnapshot.
   *
   * @param {ActivatedRouteSnapshot} route - The route snapshot.
   * @returns {string} The full route path.
   */
  getRoutePath(route: ActivatedRouteSnapshot): string {
    let routePath = '';
    if (route.routeConfig) {
      routePath += route.routeConfig.path;
    }
    if (route.firstChild) {
      routePath += this.getRoutePath(route.firstChild);
    }
    return routePath;
  }

  /**
   * Formats a numeric amount into a currency string.
   *
   * @param {number} amount - The amount to format.
   * @returns {string} The formatted currency string.
   */
  formatAmount(amount: number): string {
    const formatted = new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    return `$${formatted}`;
  }

  /**
   * Formats a date object into a string in 'MM/DD/YYYY HH:mm:ss' format.
   *
   * @param {Date} date - The date to format.
   * @returns {string} The formatted date string.
   */
  formatDate(date: Date): string {
    const newDate: Date = new Date(date);
    const year = newDate.getFullYear();
    const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
    const day = newDate.getDate().toString().padStart(2, '0');
    const hour = newDate.getHours().toString().padStart(2, '0');
    const minute = newDate.getMinutes().toString().padStart(2, '0');
    const second = newDate.getSeconds().toString().padStart(2, '0');

    return `${month}/${day}/${year} ${hour}:${minute}:${second}`;
  }
}
